/**
 * Grand Quack Auto — NPC speech bubbles.
 *
 * "For comedy — the viral fuel — add: text bubbles ('IS THAT A DUCK?!' /
 * 'CALL THE POLICE!'), panic chains, and NPCs taking photos of the duck."
 * — research.md §3
 *
 * Floating CSS speech bubbles above scared NPCs. Self-removing after 2s.
 */

import * as THREE from 'three';
import type { World } from '../types';

// ── Scared reactions (randomized per NPC) ─────────────────────────────────

const SCARED_LINES = [
  'IS THAT A DUCK?!',
  'CALL THE POLICE!',
  'AAAAHHH!!!',
  'OH NO A DUCK!!',
  'RUN FOR YOUR LIVES!',
  'SOMEONE STOP THAT DUCK!',
  'MY SANDWICH!!',
  'NOT AGAIN!!',
  'IT HONKED AT ME!',
  'DUCK!! DUUUCK!!',
  'HELP! DUCK ATTACK!',
  'TELL MY WIFE I LOVE HER!',
  'WHAT THE QUACK?!',
  'CODE RED! CODE RED!',
];

const IDLE_LINES = [
  'Nice day...',
  '*whistling*',
  'Is that a... duck?',
  'What a strange bird.',
  'Hey there lil guy!',
];

// ── Show a speech bubble above a world position ───────────────────────────

const activeBubbles: HTMLElement[] = [];
const MAX_BUBBLES = 6; // don't spam too many

export function showSpeechBubble(
  world: World,
  worldPos: THREE.Vector3,
  type: 'scared' | 'idle' = 'scared',
): void {
  if (activeBubbles.length >= MAX_BUBBLES) return;

  const hud = document.getElementById('hud');
  if (!hud) return;

  const lines = type === 'scared' ? SCARED_LINES : IDLE_LINES;
  const text = lines[Math.floor(Math.random() * lines.length)]!;

  // Project world position to screen.
  const pos = worldPos.clone();
  pos.y += 1.8; // above NPC head
  pos.project(world.camera);

  // Skip if behind camera.
  if (pos.z > 1) return;

  const x = (pos.x * 0.5 + 0.5) * window.innerWidth;
  const y = (-pos.y * 0.5 + 0.5) * window.innerHeight;

  const el = document.createElement('div');
  el.className = 'speech-bubble';
  el.textContent = text;
  el.style.left = `${x}px`;
  el.style.top = `${y}px`;
  hud.appendChild(el);
  activeBubbles.push(el);

  // Self-remove after animation.
  const remove = () => {
    el.remove();
    const idx = activeBubbles.indexOf(el);
    if (idx >= 0) activeBubbles.splice(idx, 1);
  };
  el.addEventListener('animationend', remove);
  setTimeout(remove, 2500); // fallback
}
